/**
 * Block 13 PR-G Phase 5B — Container Экрана 2.13.3 «Готовый PDF» (ADR-0005).
 *
 * Orchestrates:
 *   1. Load `SavedContract` через `loadSavedContract(contractId)` IDB.
 *   2. Load `ContractTemplate` через `loadContractTemplate(state.templateKey)`.
 *   3. Generate PDF blob через `renderContractToPdf` (jsPDF) on mount.
 *   4. Create `URL.createObjectURL(blob)` для embed + fullscreen link.
 *   5. **Cleanup:** `URL.revokeObjectURL(url)` на unmount **И** на изменение
 *      `contractId` или `attempt` (Retry). Aibek Confirmation 2: blob alive
 *      пока user смотрит preview снимок текущего contractId; на user-initiated
 *      retry / navigation к другому договору — старый url revoked + новый
 *      создаётся. State changes внутри ContractDraft (autosave) НЕ trigger
 *      re-generate — Container не observe'ит draft state после initial load.
 *
 * **State machine:** loading → ready | not-found | error. Retry callback
 * triggers re-render attempt (bumps attempt counter).
 *
 * **Download / share wiring:**
 *   - `onDownload` — reuses already-rendered blob через `triggerContractPdfDownload`
 *     (no re-render, blob lifecycle controlled by Container).
 *   - `onShareOpen` — opens `ShareContractBottomSheet` (sheet делает свой
 *     render-and-share через `shareContractPdf` service; не reuse наш blob
 *     потому что service handles full flow incl. file attachment).
 *
 * **NO URL params reading** (per CRITICAL note "не trogаю router.tsx" — Phase 6
 * wires routes). Container принимает `contractId` через prop.
 *
 * Reference:
 *   - PRD §10.13.3
 *   - ADR-0025 §Phase 5 file list
 *   - Confirmation 2 (blob URL lifecycle: createObjectURL on mount, revoke on
 *     unmount; re-generate НЕ на state changes — snapshot)
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { ContractTemplate } from '@pomoshnik/shared';

import {
  clearCustomerInSavedContract,
  loadSavedContract,
  type SavedContract,
} from './state/savedContractsIdb';
import { loadContractTemplate } from './templates/loadContractTemplate';
import { renderContractToPdf } from './services/renderContractToPdf';
import { buildContractFilename, triggerContractPdfDownload } from './services/generateContractPdf';
import { ContractPdfPreviewView } from './ContractPdfPreviewView';
import { ShareContractBottomSheet } from './components/ShareContractBottomSheet';
import type { ContractShareResult } from './services/shareContractPdf';

export interface ContractPdfPreviewContainerProps {
  /** UUID договора из IDB. Получается parent route / props (Phase 6 router wiring). */
  contractId: string;
}

type LoadState =
  | { status: 'loading' }
  | {
      status: 'ready';
      contract: SavedContract;
      template: ContractTemplate;
      blob: Blob;
      blobUrl: string;
    }
  | { status: 'not-found' }
  | { status: 'error' };

export function ContractPdfPreviewContainer({ contractId }: ContractPdfPreviewContainerProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    async function load(): Promise<void> {
      setState({ status: 'loading' });
      try {
        const contract = await loadSavedContract(contractId);
        if (cancelled) return;
        if (!contract) {
          setState({ status: 'not-found' });
          return;
        }

        const template = loadContractTemplate(contract.state.templateKey);
        const blob = await renderContractToPdf(contract.state, template);
        // Создаём URL; затем проверяем cancelled и revoke немедленно если
        // unmount произошёл между resolve renderContractToPdf и этим кодом
        // (тонкое окно но реальное в slow-render path).
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setState({ status: 'ready', contract, template, blob, blobUrl: url });
      } catch (err) {
        if (cancelled) return;
        if (import.meta.env.DEV) {
          // P-009: log only err.name (avoid PII в render error message).
          const errName = err instanceof Error ? err.name : 'unknown';
          console.error('[ContractPdfPreviewContainer] render failed', errName);
        }
        setState({ status: 'error' });
      }
    }

    void load();

    return () => {
      cancelled = true;
      // Revoke blob URL on unmount/retry. Aibek Confirmation 2: blob alive ТОЛЬКО
      // пока user смотрит preview — revoke снимает reference, blob GC'd браузером.
      if (createdUrl !== null) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [contractId, attempt]);

  function handleDownload(): void {
    if (state.status !== 'ready') return;
    const filename = buildContractFilename(state.contract.state);
    triggerContractPdfDownload(state.blob, filename);
    // PR-B1 (ADR-0034): clear trigger перенесён с PDF-delivered на session-end.
    // Реквизиты клиента сохраняются между итерациями PDF — очистка в IDB
    // snapshot происходит на navigate-Home / beforeunload (см. session-end clear
    // ниже, если нет opt-in preserveCustomerRequisites).
  }

  function handleShareResult(_result: ContractShareResult): void {
    if (state.status !== 'ready') return;
    // PR-B1 (ADR-0034): no clear here — customer requisites сохраняются до
    // session-end (navigate-Home / beforeunload). Status semantics остаются у
    // ShareContractBottomSheet (notifications). См. ADR-0034.
  }

  function handleRetry(): void {
    setAttempt((n) => n + 1);
  }

  // PR-B1 (ADR-0034) — session-end clear реквизитов клиента в saved snapshot (IDB).
  // Триггеры: «На главную» (onBeforeNavigateHome) + beforeunload (app close). НЕ
  // на PDF/Share (перенос с ADR-0025 A9). Снимок уже на диске → IDB-clear напрямую
  // (dispatch не применим). stateRef держит последний LoadState для стабильного
  // callback без ре-подписки на beforeunload.
  const stateRef = useRef(state);
  stateRef.current = state;

  const clearCustomerRequisitesOnExit = useCallback(() => {
    const current = stateRef.current;
    if (current.status !== 'ready') return;
    if (current.contract.state.preserveCustomerRequisites) return; // opt-in — сохраняем
    // best-effort: на «На главную» IDB-write успевает; на beforeunload —
    // async-unsafe (задокументировано в ADR-0034). clearCustomerInSavedContract
    // идемпотентен (no-op если уже пусто).
    void clearCustomerInSavedContract(current.contract.id).catch((err) => {
      if (import.meta.env.DEV) {
        const errName = err instanceof Error ? err.name : 'unknown';
        console.error('[ContractPdfPreviewContainer] session-end clearCustomer failed', errName);
      }
    });
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', clearCustomerRequisitesOnExit);
    return () => window.removeEventListener('beforeunload', clearCustomerRequisitesOnExit);
  }, [clearCustomerRequisitesOnExit]);

  return (
    <>
      <ContractPdfPreviewView
        status={state.status}
        contract={state.status === 'ready' ? state.contract : undefined}
        blobUrl={state.status === 'ready' ? state.blobUrl : null}
        onDownload={handleDownload}
        onShareOpen={() => setShareOpen(true)}
        onRetry={handleRetry}
        onBeforeNavigateHome={clearCustomerRequisitesOnExit}
      />
      {state.status === 'ready' ? (
        <ShareContractBottomSheet
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          draft={state.contract.state}
          template={state.template}
          onResult={handleShareResult}
        />
      ) : null}
    </>
  );
}
